import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/hooks/useTenant'
import * as performanceAPI from '@/api/performance'
import { climateKeys } from './feedbackKeys'
import type { ClimateTrends, TeamInsights } from '@/types/performance'

// ================================
// QUERIES
// ================================

/**
 * Use Climate Trends Query
 *
 * Fetches organizational climate trends data.
 * Cached for 5 minutes - climate data is relatively stable.
 *
 * @example
 * ```tsx
 * const { data: trends, isLoading, isFetching } = useClimateTrends({
 *   period: 12,
 *   departmentId: 'dept-123'
 * })
 * ```
 */
export function useClimateTrends(params?: {
  period?: number
  departmentId?: string
}) {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: climateKeys.trends(tenantDomain!, params),
    queryFn: async (): Promise<ClimateTrends> => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await performanceAPI.getClimateTrends(tenantDomain, {
        period: params?.period || 12,
        departmentId: params?.departmentId,
      })
    },
    enabled: !!tenantDomain,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  })
}

/**
 * Use Team Insights Query
 *
 * Fetches team performance insights and recommendations.
 * Cached for 5 minutes.
 *
 * @example
 * ```tsx
 * const { data: insights, isLoading } = useTeamInsights()
 * ```
 */
export function useTeamInsights() {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: climateKeys.insights(tenantDomain!),
    queryFn: async (): Promise<TeamInsights> => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await performanceAPI.getTeamInsights(tenantDomain)
    },
    enabled: !!tenantDomain,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  })
}

// ================================
// UTILITY HOOKS
// ================================

/**
 * Invalidate Climate Cache
 *
 * Manually invalidate climate cache to force refetch.
 * Useful for manual refresh buttons or after survey completion.
 *
 * @example
 * ```tsx
 * const invalidateClimate = useInvalidateClimateCache()
 *
 * <Button onClick={() => invalidateClimate()}>
 *   Refresh Climate Data
 * </Button>
 * ```
 */
export function useInvalidateClimateCache() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return () => {
    if (!tenantDomain) return

    queryClient.invalidateQueries({
      queryKey: climateKeys.all,
    })
  }
}
